import { API_ENDPOINTS } from '../constants';
import {
  Announcement,
  AnnouncementCreateFormData,
  AnnouncementUpdateFormData,
} from '../types';

import { get, post, put, del } from './apiClient';

// Pagination types (consistent with other services)
export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  next_page: number | null;
  prev_page: number | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

export interface AnnouncementFilters {
  status?: string;
  type?: string;
  priority?: string;
  school_id?: string;
  created_by?: string;
  date_from?: string;
  date_to?: string;
  tags?: string[];
  search?: string;
  skip?: number;
  limit?: number;
}

export type AnnouncementsResponse = PaginatedResponse<Announcement>;

export interface AnnouncementAnalytics {
  summary: {
    total_announcements: number;
    sent_this_month: number;
    pending_approval: number;
    delivery_rate: number;
    engagement_rate: number;
  };
  delivery_stats: Record<string, unknown>;
  engagement_metrics: Record<string, unknown>;
  channel_performance: Record<string, unknown>;
  compliance_report: Record<string, unknown>;
}

export interface BroadcastResponse {
  announcement_id: string;
  status: string;
  message?: string;
  total_recipients?: number;
  channels: string[];
  started_at?: string;
  completed_at?: string;
  channel_results: Record<string, unknown>;
  failed_deliveries: string[];
}

// Get announcements with filters and pagination
export const getAnnouncements = async (
  filters: AnnouncementFilters = {}
): Promise<AnnouncementsResponse> => {
  const searchParams = new URLSearchParams();

  if (filters.skip !== undefined)
    searchParams.append('offset', filters.skip.toString());
  if (filters.limit !== undefined)
    searchParams.append('limit', filters.limit.toString());
  if (filters.search) searchParams.append('search', filters.search);
  if (filters.status) searchParams.append('status', filters.status);
  if (filters.type) searchParams.append('type', filters.type);
  if (filters.priority) searchParams.append('priority', filters.priority);
  if (filters.school_id) searchParams.append('school_id', filters.school_id);
  if (filters.created_by) searchParams.append('created_by', filters.created_by);
  if (filters.date_from) searchParams.append('date_from', filters.date_from);
  if (filters.date_to) searchParams.append('date_to', filters.date_to);
  if (filters.tags) {
    filters.tags.forEach(tag => searchParams.append('tags', tag));
  }

  const queryString = searchParams.toString();
  const endpoint = queryString
    ? `${API_ENDPOINTS.ANNOUNCEMENTS.LIST}?${queryString}`
    : API_ENDPOINTS.ANNOUNCEMENTS.LIST;

  const response = await get<{
    announcements: Announcement[];
    total_count: number;
    limit: number;
    offset: number;
    has_more: boolean;
  }>(endpoint);
  const page = Math.floor(response.offset / response.limit) + 1;
  const pages = Math.ceil(response.total_count / response.limit);

  return {
    data: response.announcements,
    pagination: {
      page,
      per_page: response.limit,
      total: response.total_count,
      pages,
      has_next: response.has_more,
      has_prev: page > 1,
      next_page: response.has_more ? page + 1 : null,
      prev_page: page > 1 ? page - 1 : null,
    },
  };
};

// Get single announcement by ID
export const getAnnouncement = async (id: string): Promise<Announcement> => {
  return get<Announcement>(API_ENDPOINTS.ANNOUNCEMENTS.GET(id));
};

// Create new announcement
export const createAnnouncement = async (
  data: AnnouncementCreateFormData
): Promise<Announcement> => {
  return post<Announcement>(API_ENDPOINTS.ANNOUNCEMENTS.CREATE, data);
};

// Update announcement
export const updateAnnouncement = async (
  id: string,
  data: AnnouncementUpdateFormData
): Promise<Announcement> => {
  return put<Announcement>(API_ENDPOINTS.ANNOUNCEMENTS.UPDATE(id), data);
};

// Delete announcement
export const deleteAnnouncement = async (id: string): Promise<void> => {
  return del<void>(API_ENDPOINTS.ANNOUNCEMENTS.DELETE(id));
};

// Broadcast announcement
export const broadcastAnnouncement = async (
  id: string
): Promise<BroadcastResponse> => {
  return post<BroadcastResponse>(API_ENDPOINTS.ANNOUNCEMENTS.BROADCAST(id));
};

// Approve announcement
export const approveAnnouncement = async (
  id: string,
  decision: 'approved' | 'rejected',
  comments?: string
): Promise<Announcement> => {
  return post<Announcement>(API_ENDPOINTS.ANNOUNCEMENTS.APPROVE(id), {
    decision,
    comments,
  });
};

// Get announcement analytics
export const getAnnouncementAnalytics =
  async (): Promise<AnnouncementAnalytics> => {
    return get<AnnouncementAnalytics>(API_ENDPOINTS.ANNOUNCEMENTS.ANALYTICS);
  };

// Get schools for dropdown (if needed for school selection)
export const getSchoolsForAnnouncementTargeting = async (): Promise<
  Array<{ id: string; name: string; district_name?: string }>
> => {
  const response = await get<
    PaginatedResponse<{ id: number; name: string; district?: { name: string } }>
  >(API_ENDPOINTS.SCHOOLS.LIST);

  // Transform the response to match the expected format
  return response.data.map(school => ({
    id: school.id.toString(),
    name: school.name,
    district_name: school.district?.name,
  }));
};
